import { FieldKey } from "@eu.mdotm.api/sphere";
import { atomWithJsonStorage } from "$root/third-party-integrations/jotai";
import type { OrderBy } from "@mdotm/shadcn/legacy/components";
import { deriveNestedAtom } from "@mdotm/shadcn/legacy/reactivity";
import { typedObjectValues } from "@mdotm/shadcn/legacy/utils";
import { z } from "zod";

function orderByArrayZodValidator<ColumnNames extends [string, ...string[]] = [string]>(columnNames?: ColumnNames) {
	if (columnNames) {
		return z.array(
			z.object({
				columnName: z.enum(columnNames),
				direction: z.enum(["asc", "desc"]),
			}),
		);
	} else {
		return z.array(
			z.object({
				columnName: z.string(),
				direction: z.enum(["asc", "desc"]),
			}),
		);
	}
}

export type PortfolioStudioTableSettings = {
	portfolioCompositionEnhanceOrderByName: OrderBy[];
	portfolioCompositionOrderByName: OrderBy[];
	portfolioListOrderByName: OrderBy<FieldKey>[];
	referenceListOrderByName: OrderBy[];
	universeListOrderByName: OrderBy[];
	marketViewListOrderByName: OrderBy[];
};

const settingsZodValidator = z.object({
	portfolioCompositionEnhanceOrderByName: orderByArrayZodValidator(),
	portfolioCompositionOrderByName: orderByArrayZodValidator(),
	portfolioListOrderByName: orderByArrayZodValidator(
		typedObjectValues(FieldKey) as ["NAME"] /* a bit hacky but will do for now */,
	),
	referenceListOrderByName: orderByArrayZodValidator(),
	universeListOrderByName: orderByArrayZodValidator(),
	marketViewListOrderByName: orderByArrayZodValidator(),
} satisfies Record<keyof PortfolioStudioTableSettings, unknown>);

const defaultSettings: PortfolioStudioTableSettings = {
	portfolioCompositionOrderByName: [],
	portfolioCompositionEnhanceOrderByName: [],
	portfolioListOrderByName: [{ columnName: "INCEPTION_DATE", direction: "asc" }],
	referenceListOrderByName: [],
	universeListOrderByName: [],
	marketViewListOrderByName: [],
};

export const portfolioStudioTableSettings$ = atomWithJsonStorage(
	localStorage,
	"portfolioStudioTableSettingsAtom",
	defaultSettings,
	{
		fromPrimitives: (x) => settingsZodValidator.parse(x) as PortfolioStudioTableSettings,
	},
);

export const portfolioCompositionOrderByName$ = deriveNestedAtom(
	portfolioStudioTableSettings$,
	"portfolioCompositionOrderByName",
);
export const portfolioCompositionEnhanceOrderByName$ = deriveNestedAtom(
	portfolioStudioTableSettings$,
	"portfolioCompositionEnhanceOrderByName",
);
export const portfolioListOrderByName$ = deriveNestedAtom(portfolioStudioTableSettings$, "portfolioListOrderByName");
export const referenceListOrderByName$ = deriveNestedAtom(portfolioStudioTableSettings$, "referenceListOrderByName");
export const universeListOrderByName$ = deriveNestedAtom(portfolioStudioTableSettings$, "universeListOrderByName");
export const marketViewListOrderByName$ = deriveNestedAtom(portfolioStudioTableSettings$, "marketViewListOrderByName");
